import React, { useState } from 'react';
import { ArrowDown, ArrowRight, ArrowUp, FolderOpen, Gamepad2, LayoutGrid, LayoutList, Library, Plus, Star, Trash2, Trophy, X } from 'lucide-react';
import StatsCards from './StatsCards';
import FilterBar from './FilterBar';
import GameCard from './GameCard';
import { GameGridSkeleton, StatCardsSkeleton } from './Skeletons';
import { getAvailableYears } from '../utils/exportUtils';
import { getYearRetrospective, getMonthlySummary } from '../utils/gameUtils';

const SectionHeading = ({ kicker, title, action }) => (
  <div className="flex items-end justify-between gap-4 mb-5">
    <div>
      <p className="eyebrow mb-1.5">{kicker}</p>
      <h2 className="retro-section-title text-white">{title}</h2>
    </div>
    {action}
  </div>
);

const PlayingFeature = ({ game, onOpen }) => (
  <button onClick={() => onOpen(game)} className="retro-frame group relative w-full min-h-[250px] sm:min-h-[310px] overflow-hidden border border-white/[0.09] text-left">
    {game.coverImage ? <img src={game.coverImage} alt="" decoding="async" className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-105" /> : <div className="absolute inset-0 bg-[#171b24]" />}
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/10" />
    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
    <div className="relative p-6 sm:p-9 max-w-lg flex flex-col justify-end min-h-[250px] sm:min-h-[310px]">
      <span className="retro-label w-fit text-[9px] font-extrabold uppercase tracking-[.08em] bg-lime-400 text-black px-3 py-2 rounded-sm mb-4 border-2 border-lime-200 shadow-[3px_3px_0_#314604]">Jogando agora</span>
      <h3 className="retro-heading text-lg sm:text-2xl font-extrabold tracking-normal">{game.name}</h3>
      <div className="retro-text flex items-center gap-2 text-xl text-[#8db5d1] mt-3">
        <span>{game.platform}</span><span className="text-slate-600">•</span><span>{game.rating}/5</span>
      </div>
      <span className="retro-label flex items-center gap-2 text-[10px] font-bold text-lime-300 mt-5">Ver detalhes <ArrowRight className="w-4 h-4 transition group-hover:translate-x-1" /></span>
    </div>
  </button>
);

export const HomeView = ({ user, games, stats, loading, onAdd, onLibrary, onOpen }) => {
  const playing = games.filter(game => game.status === 'playing');
  const recent = games.slice(0, 4);
  const firstName = user.displayName?.split(' ')[0] || 'jogador';
  const monthly = getMonthlySummary(games);
  return (
    <main className="retro-page app-container py-7 sm:py-10 pb-28 md:pb-14 space-y-12">
      <section className="retro-panel p-5 sm:p-9">
        <p className="retro-text text-[#8db5d1] text-xl sm:text-2xl font-semibold mb-2">Olá, {firstName}</p>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <h1 className="retro-display font-extrabold max-w-3xl">Qual história vamos continuar hoje?</h1>
          <button onClick={onAdd} className="retro-pixel-button btn-primary px-5 py-4 text-[10px] flex items-center justify-center gap-2 shrink-0"><Plus className="w-4 h-4" /> Adicionar jogo</button>
        </div>
      </section>

      {loading ? <div className="min-h-[250px] sm:min-h-[310px] rounded-[24px] skeleton" aria-hidden="true" /> :
        playing[0] ? <PlayingFeature game={playing[0]} onOpen={onOpen} /> : (
        <section className="relative overflow-hidden rounded-[28px] border border-dashed border-white/[0.12] bg-white/[0.025] p-8 sm:p-12 text-center">
          <Gamepad2 className="w-9 h-9 text-lime-400 mx-auto mb-4" />
          <h2 className="text-xl font-extrabold">Nenhum jogo em andamento</h2>
          <p className="text-sm text-slate-500 mt-2">Adicione um jogo ou altere o status de um item da sua biblioteca.</p>
          <button onClick={onAdd} className="btn-primary px-5 py-3 text-sm mt-5">Adicionar primeiro jogo</button>
        </section>
      )}

      <section>
        <SectionHeading kicker="Resumo rápido" title="Seu momento" />
        {loading ? <StatCardsSkeleton /> : <StatsCards stats={stats} games={games} compact />}
      </section>

      {!loading && (monthly.startedCount > 0 || monthly.finishedCount > 0) && (
        <section>
          <SectionHeading kicker="Este mês" title={`Resumo de ${monthly.monthLabel}`} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Iniciados" value={monthly.startedCount} />
            <MiniStat label="Finalizados" value={monthly.finishedCount} />
            <MiniStat label="Horas registradas" value={monthly.hours || 0} />
            <MiniStat label="Destaque" value={monthly.highlight?.name || '—'} small />
          </div>
        </section>
      )}

      {recent.length > 0 && <section>
        <SectionHeading kicker="Últimos registros" title="Adicionados recentemente" action={<button onClick={onLibrary} className="text-xs font-bold text-lime-400 flex items-center gap-1.5">Ver todos <ArrowRight className="w-3.5 h-3.5" /></button>} />
        <div className="grid md:grid-cols-2 gap-4">{recent.map(game => <GameCard key={game.id} game={game} onEdit={onOpen} minimal />)}</div>
      </section>}
    </main>
  );
};

const MiniStat = ({ label, value, small }) => (
  <div className="surface p-4">
    <div className={`font-extrabold text-white ${small ? 'text-sm truncate' : 'text-xl'}`}>{value}</div>
    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mt-1">{label}</div>
  </div>
);

const QueuePanel = ({ games, onMove, onRemove, onOpen }) => (
  <section className="mb-7">
    <SectionHeading kicker={`${games.length}/5`} title="Jogar em seguida" />
    <div className="space-y-2">
      {games.map((game, index) => (
        <div key={game.id} className="flex items-center gap-3 p-2.5 rounded-2xl border border-white/[0.07] bg-white/[0.025]">
          <span className="w-6 text-center text-xs font-extrabold text-lime-400">{index + 1}</span>
          <button onClick={() => onOpen(game)} className="min-w-0 flex-1 text-left">
            <div className="font-bold text-sm truncate">{game.name}</div>
            <div className="text-[10px] text-slate-600">{game.platform}</div>
          </button>
          <button onClick={() => onMove(game.id, -1)} disabled={index === 0} className="icon-button !min-w-8 !min-h-8 disabled:opacity-30" aria-label="Mover para cima"><ArrowUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => onMove(game.id, 1)} disabled={index === games.length - 1} className="icon-button !min-w-8 !min-h-8 disabled:opacity-30" aria-label="Mover para baixo"><ArrowDown className="w-3.5 h-3.5" /></button>
          <button onClick={() => onRemove(game.id)} className="icon-button !min-w-8 !min-h-8 hover:!text-red-300" aria-label="Remover da fila"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  </section>
);

export const LibraryView = ({
  games, filteredGames, loading, filters, onOpen, onDelete, onAdd, onToggleFavorite,
  viewMode, onChangeViewMode, trashCount, onOpenTrash, onOpenCollections,
  queuedGames = [], onMoveQueueItem, onRemoveFromQueue, onOpenQueueGame
}) => (
  <main className="retro-page app-container py-7 sm:py-10 pb-28 md:pb-14">
    <section className="flex items-start sm:items-end justify-between gap-4 mb-7 flex-wrap">
      <div>
        <p className="eyebrow mb-1.5">Sua coleção</p>
        <h1 className="retro-page-title font-extrabold">Biblioteca</h1>
        <p className="text-slate-500 text-sm mt-2">{games.length} {games.length === 1 ? 'jogo registrado' : 'jogos registrados'}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex rounded-xl border border-white/[0.08] overflow-hidden" role="group" aria-label="Modo de visualização">
          <button onClick={() => onChangeViewMode('grid')} aria-pressed={viewMode === 'grid'} className={`p-2.5 ${viewMode === 'grid' ? 'bg-lime-400 text-black' : 'bg-white/[0.03] text-slate-400'}`} aria-label="Visualização em grade"><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => onChangeViewMode('compact')} aria-pressed={viewMode === 'compact'} className={`p-2.5 ${viewMode === 'compact' ? 'bg-lime-400 text-black' : 'bg-white/[0.03] text-slate-400'}`} aria-label="Visualização compacta"><LayoutList className="w-4 h-4" /></button>
        </div>
        <button onClick={onOpenCollections} className="btn-secondary px-3.5 py-2.5 text-xs flex items-center gap-2"><FolderOpen className="w-3.5 h-3.5" /> Coleções</button>
        <button onClick={onOpenTrash} className="btn-secondary px-3.5 py-2.5 text-xs flex items-center gap-2"><Trash2 className="w-3.5 h-3.5" /> Lixeira{trashCount > 0 ? ` (${trashCount})` : ''}</button>
        <button onClick={onAdd} className="hidden sm:flex btn-primary px-5 py-3 text-sm items-center gap-2"><Plus className="w-4 h-4" /> Adicionar</button>
      </div>
    </section>

    {queuedGames.length > 0 && (
      <QueuePanel games={queuedGames} onMove={onMoveQueueItem} onRemove={onRemoveFromQueue} onOpen={onOpenQueueGame} />
    )}

    <FilterBar {...filters} />
    {loading ? <GameGridSkeleton /> :
      filteredGames.length === 0 ? <Empty icon={Library} title={games.length ? 'Nenhum resultado encontrado' : 'Sua biblioteca está vazia'} subtitle={games.length ? 'Tente remover alguns filtros.' : 'Adicione seu primeiro jogo para começar.'} /> :
      viewMode === 'compact' ? (
        <div className="space-y-2">{filteredGames.map(game => <GameCard key={game.id} game={game} onEdit={onOpen} onToggleFavorite={onToggleFavorite} compact />)}</div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">{filteredGames.map(game => <GameCard key={game.id} game={game} onEdit={onOpen} onDelete={onDelete} onToggleFavorite={onToggleFavorite} />)}</div>
      )}
  </main>
);

const Empty = ({ icon:Icon, title, subtitle }) => <div className="py-20 text-center border border-dashed border-white/[0.1] rounded-[28px]"><Icon className="w-8 h-8 text-slate-700 mx-auto mb-3" /><h3 className="font-bold text-slate-300">{title}</h3>{subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}</div>;

const Retrospective = ({ games }) => {
  const years = getAvailableYears(games);
  const [year, setYear] = useState(years[0] || new Date().getFullYear());
  if (years.length === 0) return null;
  const data = getYearRetrospective(games, year);

  return (
    <section>
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div>
          <p className="eyebrow mb-1.5">Sua retrospectiva</p>
          <h2 className="retro-section-title text-white">Meu ano em jogos</h2>
        </div>
        <select value={year} onChange={e => setYear(Number(e.target.value))} className="input-field w-auto" style={{ colorScheme:'dark' }}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <MiniStat label="Finalizados" value={data.completedCount} />
        <MiniStat label="Platinados" value={data.platinadosCount} />
        <MiniStat label="Plataforma preferida" value={data.topPlatform || '—'} small />
        <MiniStat label="Gênero preferido" value={data.topGenre || '—'} small />
        <MiniStat label="Melhor avaliado" value={data.bestRated?.name || '—'} small />
        <MiniStat label="Primeiro jogo do ano" value={data.firstGame?.name || '—'} small />
      </div>
    </section>
  );
};

export const StatsView = ({ stats, games, onExport, onOpen }) => {
  const platinum = games.filter(game => game.platinado);
  const favorites = games.filter(game => game.favorite);
  return <main className="retro-page app-container py-7 sm:py-10 pb-28 md:pb-14 space-y-12">
    <section className="flex items-end justify-between gap-4"><div className="min-w-0"><p className="eyebrow mb-2">Sua jornada</p><h1 className="retro-page-title font-extrabold">Estatísticas</h1><p className="retro-text text-[#8db5d1] text-lg sm:text-xl mt-2">Um retrato da sua vida em jogos.</p></div><button onClick={onExport} className="btn-secondary shrink-0 px-3 sm:px-4 py-3 text-[10px] sm:text-xs leading-tight">Exportar<br className="sm:hidden" /> ranking</button></section>
    <StatsCards stats={stats} games={games} />

    <Retrospective games={games} />

    <section>
      <SectionHeading kicker="Favoritos" title="Salão das Lendas" />
      {favorites.length ? <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-thin">{favorites.map(game => <button key={game.id} onClick={() => onOpen?.(game)} className="relative shrink-0 w-36 sm:w-44 text-left group"><div className="relative aspect-[3/4] rounded-[24px] overflow-hidden border border-white/10 bg-[#171b24] isolate">{game.coverImage ? <><img src={game.coverImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-50" /><img src={game.coverImage} alt={`Capa de ${game.name}`} loading="lazy" decoding="async" className="relative w-full h-full object-contain group-hover:scale-[1.025] transition duration-500" /></> : <div className="w-full h-full bg-[#171b24]" />}</div><span className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full bg-amber-400 text-black grid place-items-center"><Star className="w-4 h-4 fill-current" /></span><div className="font-bold text-sm mt-2.5 truncate">{game.name}</div></button>)}</div> : <Empty icon={Star} title="Nenhum favorito ainda" subtitle="Marque jogos com a estrela para reuni-los aqui." />}
    </section>

    <section>
      <SectionHeading kicker="Conquistas" title="Hall dos platinados" />
      {platinum.length ? <div className="flex gap-4 overflow-x-auto pb-3 scrollbar-thin">{platinum.map((game,index) => <button key={game.id} onClick={() => onOpen?.(game)} className="relative shrink-0 w-36 sm:w-44 text-left group"><div className="relative aspect-[3/4] rounded-[24px] overflow-hidden border border-white/10 bg-[#171b24] isolate">{game.coverImage ? <><img src={game.coverImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-50" /><img src={game.coverImage} alt={`Capa de ${game.name}`} loading="lazy" decoding="async" className="relative w-full h-full object-contain group-hover:scale-[1.025] transition duration-500" /></> : <div className="w-full h-full bg-[#171b24]" />}</div><span className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full bg-lime-400 text-black grid place-items-center font-extrabold text-xs">{index + 1}</span><div className="font-bold text-sm mt-2.5 truncate">{game.name}</div><div className="flex items-center gap-1 text-[10px] text-lime-400 font-bold mt-1"><Trophy className="w-3 h-3" /> 100% completo</div></button>)}</div> : <Empty icon={Trophy} title="Nenhum platinado ainda" subtitle="Seus jogos 100% completos aparecerão aqui." />}
    </section>
  </main>;
};

export const CollectionsPanel = ({ lists, games, onDeleteList, onClose, onOpenGame }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end sm:items-center sm:justify-center sm:p-5" role="dialog" aria-modal="true" aria-label="Minhas coleções">
      <button className="absolute inset-0 w-full" onClick={onClose} aria-label="Fechar coleções" />
      <div className="relative w-full sm:max-w-lg max-h-[85vh] overflow-y-auto surface-raised rounded-b-none sm:rounded-b-2xl p-5 sm:p-6 animate-slideUp">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-extrabold flex items-center gap-2"><FolderOpen className="w-5 h-5 text-lime-400" /> Minhas coleções</h2>
          <button onClick={onClose} className="icon-button" aria-label="Fechar"><X className="w-4 h-4" /></button>
        </div>
        {lists.length === 0 ? (
          <p className="text-sm text-slate-500 py-8 text-center">Crie coleções a partir da tela de detalhes de um jogo.</p>
        ) : (
          <div className="space-y-4">
            {lists.map(list => {
              const listGames = list.gameIds.map(id => games.find(g => g.id === id)).filter(Boolean);
              return (
                <div key={list.id} className="p-3.5 rounded-2xl border border-white/[0.07] bg-white/[0.025]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-sm">{list.name} <span className="text-slate-600 font-normal">({listGames.length})</span></h3>
                    <button onClick={() => onDeleteList(list.id)} className="icon-button !min-w-8 !min-h-8 hover:!text-red-300" aria-label={`Excluir coleção ${list.name}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                  {listGames.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {listGames.map(game => <button key={game.id} onClick={() => onOpenGame(game)} className="px-2.5 py-1 rounded-lg bg-white/[0.05] text-xs font-medium hover:bg-white/[0.09]">{game.name}</button>)}
                    </div>
                  ) : <p className="text-xs text-slate-600">Nenhum jogo adicionado ainda.</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
